<template>
  <div class="flex justify-end">
    <div
      v-if="searchScope === 'streams'"
      class="flex-1"
    >
      <StreamsByGroupPicker v-model="searchStreams" />
    </div>
    <QBtn
      class="q-px-xs q-mx-xs"
      :class="{ 'bold rounded': filterApplied }"
      color="primary"
      dense
      :flat="!filterApplied"
      icon="filter_list"
      :label="btnLabel"
      @click="open"
    />

    <QDialog
      v-model="show"
      position="bottom"
      square
    >
      <QCard class="full-width">
        <div class="header q-pa-md flex items-center justify-between">
          <span class="title bold block">{{
            $t("universal_search_actionsheet_filters.filter_options")
          }}</span>
          <div class="q-gutter-sm">
            <QBtn
              class="font-small"
              dense
              flat
              :label="$t('universal_search_actionsheet_filters.reset_filters')"
              @click="resetFilters"
            />
            <QBtn
              class="font-small"
              dense
              flat
              :label="$t('universal_search_actionsheet_filters.done')"
              @click="close"
            />
          </div>
        </div>
        <ul class="list">
          <li
            v-if="searchScope !== 'journalists'"
            class="q-px-md q-py-sm flex items-center"
          >
            <QIcon
              class="icon"
              :class="{
                active: searchRange.toString() !== defaultDateRange.toString(),
              }"
              name="ion-time"
            />
            <RangePickerActionSheet
              v-model="searchRange"
              class="flex-1"
            />
          </li>
          <li class="q-px-md q-py-sm flex items-center">
            <QIcon
              class="icon"
              :class="{
                active: selectedMediaTypes.length !== mediaTypeSources.length,
              }"
              name="ion-options"
            />
            <UniversalSearchActionsheetSelectMediaTypes
              v-model="searchStream"
              class="flex-1"
              :excluded-media="excludedMedia"
            />
          </li>
          <li class="q-px-md q-py-sm flex items-center">
            <QIcon
              class="icon"
              :class="{ active: searchSources.length }"
              name="filter_alt"
            />
            <UniversalSearchActionsheetSelectSourceFilter
              v-model="searchSources"
              class="flex-1"
              :stream="searchStream"
            />
          </li>
        </ul>
      </QCard>
    </QDialog>
  </div>
</template>

<script>
import RangePickerActionSheet from "shared/components/core/pickers/RangePickerActionSheet.vue";
import StreamsByGroupPicker from "shared/components/core/pickers/StreamsByGroupPicker.vue";
import DateRange from "shared/helpers/DateRange";
import { getMediaForStream, getSources } from "shared/helpers/media";

import UniversalSearchActionsheetSelectMediaTypes from "./UniversalSearchActionsheetSelectMediaTypes.vue";
import UniversalSearchActionsheetSelectSourceFilter from "./UniversalSearchActionsheetSelectSourceFilter.vue";

export default {
  name: "UniversalSearchActionsheetFilters",
  components: {
    RangePickerActionSheet,
    StreamsByGroupPicker,
    UniversalSearchActionsheetSelectMediaTypes,
    UniversalSearchActionsheetSelectSourceFilter,
  },
  props: {
    stream: {
      type: Object,
      required: true,
    },
    range: {
      type: DateRange,
      required: true,
    },
    streams: {
      type: Array,
      required: true,
    },
    sources: {
      type: Array,
      required: true,
    },
    searchScope: {
      type: String,
      required: true,
    },
    excludedMedia: {
      type: Array,
      default: () => [],
    },
  },
  emits: [
    "update:stream",
    "update:range",
    "update:streams",
    "update:sources",
    "search",
  ],
  data() {
    return {
      show: false,
      defaultDateRange: DateRange.lastSevenDays(),
      mediaTypeSources: getSources(),
    };
  },
  computed: {
    searchStream: {
      get() {
        return this.stream;
      },
      set(value) {
        this.$emit("update:stream", value);
        this.$emit("search");
      },
    },
    searchRange: {
      get() {
        return this.range;
      },
      set(value) {
        this.$emit("update:range", value);
        this.$emit("search");
      },
    },
    searchStreams: {
      get() {
        return this.streams;
      },
      set(value) {
        this.$emit("update:streams", value);
        this.$emit("search");
      },
    },
    searchSources: {
      get() {
        return this.sources;
      },
      set(value) {
        this.$emit("update:sources", value);
        this.$emit("search");
      },
    },
    filterApplied() {
      return (
        this.selectedMediaTypes.length !== this.mediaTypeSources.length ||
        this.defaultDateRange.toString() !== this.searchRange.toString() ||
        this.searchSources.length
      );
    },
    btnLabel() {
      if (this.filterApplied)
        return this.$t("universal_search_actionsheet_filters.filters_applied");

      return this.$t("universal_search_actionsheet_filters.add_filter");
    },
    selectedMediaTypes() {
      return getMediaForStream(this.searchStream);
    },
  },
  methods: {
    open() {
      this.show = true;
    },
    close() {
      this.show = false;
    },
    resetFilters() {
      this.searchRange = this.defaultDateRange;
      this.searchSources = [];

      const stream = { ...this.stream };

      this.mediaTypeSources.forEach((source) => {
        stream[`${source.field}_content`] = true;
      });

      this.searchStream = stream;
      this.searchScope = "select";
    },
  },
};
</script>

<style lang="scss" scoped>
.q-btn :deep(.on-left) {
  margin-right: 4px;
}

.flex-1 {
  flex: 1;
}

.font-small {
  font-size: 12px;
}

.rounded {
  border-radius: 2px;
}

.icon {
  color: #9b9b9b;

  &.active {
    color: #444;
  }
}

.header {
  border-bottom: 1px solid #ddd;

  .title {
    font-size: 19px;
  }
}

.list {
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    border-bottom: 1px solid #ddd;
  }
}
</style>
